import React from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { Link } from "react-router-dom";

/*
 * ZelTechnology Digital Growth — mission-aligned earned income.
 * SEO + ad-platform onboarding services. Free for other nonprofits.
 * NOTE: pricing copy is intentionally modest — adjust rates when ready.
 */

const services = [
  {
    icon: "🔍",
    title: "SEO Audit & Roadmap",
    tag: "Most popular",
    items: [
      "Full technical check: speed, mobile, indexing, broken links",
      "Keyword research for YOUR audience",
      "A prioritized 90-day action roadmap in plain English",
      "One follow-up session to review progress",
    ],
  },
  {
    icon: "💰",
    title: "Ad Platform Onboarding",
    tag: "Real experience",
    items: [
      "Google AdSense, Mediavine, and Ezoic — we know the requirements",
      "Site prep: the exact things reviewers look for (we did it for our own site)",
      "Application walkthrough and follow-through until approval",
      "Ad placement strategy that respects your visitors",
    ],
  },
  {
    icon: "📈",
    title: "Content & Growth Strategy",
    tag: "Ongoing",
    items: [
      "Content plan built around what your audience actually searches",
      "Monthly check-ins and progress reporting",
      "Analytics setup so you can see growth yourself",
    ],
  },
];

const DigitalGrowth = () => {
  useDocTitle("ZelT | Digital Growth Services");

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20">
        <div className="mx-4 max-w-6xl mx-auto">
          {/* hero */}
          <div className="text-center mb-12" data-aos="zoom-in">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-bold bg-teal-100 text-blue-900 border border-teal-400 mb-4">
              A ZELTECHNOLOGY SERVICE
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-blue-900 mb-4">
              ZelTechnology <span className="text-teal-500">Digital Growth</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Websites don't grow by accident. We help small businesses,
              creators, and nonprofits get found on Google and turn their
              content into revenue — with honest advice at nonprofit prices.
            </p>
          </div>

          {/* credibility strip */}
          <div
            className="mb-12 rounded-2xl p-6 text-center text-white shadow-xl"
            style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}
            data-aos="fade-up"
          >
            <p className="font-semibold">
              🦈 Why trust us? We did it for ourselves first —{" "}
              <span className="font-bold">
                this very website was prepped, optimized, and prepped for
                ad-platform review
              </span>{" "}
              using the same process we'll walk you through.
            </p>
          </div>

          {/* services */}
          <div className="grid gap-8 md:grid-cols-3 mb-14">
            {services.map((s) => (
              <div
                key={s.title}
                className="relative bg-gray-50 rounded-2xl p-6 shadow-lg border-t-4 border-teal-400 flex flex-col"
                data-aos="fade-up"
              >
                <span className="absolute -top-3 right-4 bg-blue-900 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {s.tag}
                </span>
                <span className="text-4xl mb-3">{s.icon}</span>
                <h2 className="text-xl font-extrabold text-blue-900 mb-4">
                  {s.title}
                </h2>
                <ul className="space-y-2 text-gray-600 text-sm flex-1">
                  {s.items.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="text-teal-500 font-bold">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* nonprofit pledge */}
          <div
            className="mb-14 rounded-2xl p-8 shadow-xl text-white"
            style={{ background: "linear-gradient(90deg, #36395a, #1e3a8a)" }}
            data-aos="fade-up"
          >
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h2 className="text-2xl font-extrabold mb-2">
                  🤝 Free for other nonprofits
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  Churches, charities, and fellow nonprofits get our Digital
                  Growth services at no cost. When your website earns ad
                  revenue, your mission grows — that's the whole point.
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-300 text-sm mb-3">
                  For businesses, pricing is modest and quoted per project —
                  every paid engagement subsidizes free work for nonprofits.
                </p>
                <p className="font-bold text-teal-300">
                  Every project funds the mission.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mb-10" data-aos="fade-up">
            <h2 className="text-2xl font-extrabold text-blue-900 mb-3">
              Let's look at your site together
            </h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              Start with a free 30-minute consultation. No jargon, no
              obligation — just an honest look at what's working and what
              isn't.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-block px-6 py-3 rounded-xl bg-blue-900 text-white font-bold hover:bg-blue-800 transition"
              >
                Book a Free Consultation
              </Link>
              <Link
                to="/faq"
                className="inline-block px-6 py-3 rounded-xl border-2 border-blue-900 text-blue-900 font-bold hover:bg-blue-50 transition"
              >
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DigitalGrowth;
